import {
  ArrowRightFromLine,
  ArrowUpRight,
  CopyIcon,
  MapPinIcon,
  CalendarIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

import EventContainer from "@/components/public/event-container";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "@/components/ui/sheet";
import { safeJsonParse } from "@/lib/safeJsonParse";
import useSwipe from "@/lib/useSwipe";
import { cn } from "@/lib/utils";
import { DateTime } from "luxon";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import { useCopyToClipboard } from "usehooks-ts";
import { Button } from "../ui/button";
import { OrganizationBadge } from "./organization-badge";
import Poster from "./poster";
import usePreventBackNavigation from "@/lib/usePreventBackNavigation";
import { Badge } from "../ui/badge";

export default function MovieEventCard({ event, className }: any) {
  const t = useTranslations("EventCard");
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [open, setOpen] = useState(false);
  const swipeHandlers = useSwipe({
    onSwipedLeft: () => null,
    onSwipedRight: () => setOpen(false),
  });

  usePreventBackNavigation(open, () => setOpen(false));

  // Filter only upcoming projections (today or later)
  const today = DateTime.now().startOf("day");
  const upcomingProjections =
    event.projections?.filter((projection: any) => {
      const projectionDate = DateTime.fromISO(projection.date);
      return projectionDate >= today;
    }) || [];

  // If no upcoming projections, don't show the card
  if (upcomingProjections.length === 0) {
    return null;
  }

  // Sort projections by date
  const sortedProjections = upcomingProjections.sort(
    (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Find the first and last projection dates to create the interval
  const firstProjection = sortedProjections[0];
  const lastProjection = sortedProjections[sortedProjections.length - 1];

  const startDate = DateTime.fromISO(firstProjection.date);
  const endDate = DateTime.fromISO(lastProjection.date);

  // Format the interval
  const dateInterval =
    startDate.toFormat("d LLL") +
    (startDate.toFormat("yyyy-MM-dd") !== endDate.toFormat("yyyy-MM-dd")
      ? " - " + endDate.toFormat("d LLL")
      : "");

  return (
    <div {...swipeHandlers}>
      <Sheet onOpenChange={setOpen} open={open}>
        <SheetTrigger asChild>
          <div
            className={cn(
              className,
              "flex flex-row justify-between rounded-2xl bg-background/80 p-3 backdrop-blur transition-all duration-100 border border-[#131517] border-opacity-0 hover:border-opacity-10 supports-[backdrop-filter]:bg-background/60 dark:border-opacity-10 dark:border-white dark:hover:border-white dark:hover:border-opacity-20 cursor-pointer hover:shadow-[0_0_10px_0_rgba(0,0,0,0.05)]",
            )}
          >
            <div className="flex flex-col items-start content-start justify-between gap-2">
              <h2 className="font-semibold tracking-tight md:text-lg scroll-m-20 md:font-medium first-letter:uppercase">
                {event.name}
              </h2>
              <div className="flex flex-row gap-2 opacity-50">
                <OrganizationBadge organization={event.organization} />
              </div>
              <div className="flex flex-row items-center gap-1 opacity-50">
                <MapPinIcon className="w-4 h-4 text-muted-foreground" />
                <div className="text-sm font-normal text-muted-foreground first-letter:uppercase">
                  {event.hiddenVenue || event.venue ? (
                    <>
                      {event.hiddenVenue && <>{t("secret-location")}</>}
                      {!event.hiddenVenue && safeJsonParse(event.venue)?.name}
                    </>
                  ) : (
                    <>{t("unknown-location")}</>
                  )}
                </div>
              </div>
              <div className="flex flex-row items-center gap-1 mt-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <Badge
                  variant="outline"
                  className="px-2 py-0 text-xs"
                  suppressHydrationWarning
                >
                  {dateInterval}
                </Badge>
              </div>
            </div>
            <Poster
              event={event}
              className={cn(
                "justify-center flex opacity-95 max-w-24 md:max-w-32 md:max-h-32 aspect-square md:min-w-32",
              )}
            ></Poster>
          </div>
        </SheetTrigger>
        <SheetContent
          side={"roundedRight"}
          className="overflow-hidden sm:max-w-xl no-scrollbar p-0 rounded-lg pb-10 border-none w-[90vw]"
        >
          <SheetHeader className="absolute top-0 z-50 w-full bg-background/0 backdrop-blur supports-[backdrop-filter]:bg-background/0 print:hidden flex flex-row justify-between items-center space-y-0 p-2">
            <Button
              variant={"ghost"}
              size={"icon"}
              className="bg-background/30"
              onClick={() => setOpen(false)}
            >
              <ArrowRightFromLine className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <div className="flex space-x-2">
              <Button
                className="flex flex-row gap-2 bg-background/30"
                variant={"ghost"}
                onClick={(e: any) =>
                  copyToClipboard(
                    process.env.BASE_UI_URL +
                      "/events/" +
                      (event.isExt ? "ext_" : "") +
                      event.id,
                  ).then((success) => {
                    if (success) {
                      toast.success(
                        "Event link copied to clipboard successfully.",
                      );
                    } else {
                      console.error("Failed to copy text to clipboard.");
                    }
                  })
                }
              >
                <CopyIcon className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:block">{t("copy-link")}</span>
              </Button>
              <Button
                asChild
                variant={"outline"}
                className="border-none bg-background/30"
              >
                <Link
                  href={
                    event.isExt &&
                    event.link.startsWith("https://dashboard.bip.events")
                      ? event.link
                      : "/events/" + (event.isExt ? "ext_" : "") + event.id
                  }
                >
                  {t("go-to-event-website")}
                  <ArrowUpRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Link>
              </Button>
            </div>
          </SheetHeader>
          <EventContainer
            event={event}
            inSheet={true}
            className="h-full mt-10 overflow-y-scroll"
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
